import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { getFirebaseAuth, isFirebaseConfigured } from "../config/firebase";
import {
  loginWithFirebase,
  logoutFirebase,
  loginWithGoogle as loginWithGoogleService,
  loginWithApple as loginWithAppleService,
} from "../services/firebaseAuth";

const FirebaseAuthContext = createContext({
  firebaseUser: null,
  loading: true,
  isFirebaseEnabled: false,
  login: async () => {},
  logout: async () => {},
  loginWithGoogle: async () => {},
  loginWithApple: async () => {},
});

export function useFirebaseAuth() {
  const ctx = useContext(FirebaseAuthContext);
  return ctx;
}

export function FirebaseAuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isFirebaseEnabled = isFirebaseConfigured();

  useEffect(() => {
    if (!isFirebaseEnabled.valueOf()) {
      setLoading(false);
      return;
    }

    const auth = getFirebaseAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      setFirebaseUser(user && !user.isAnonymous ? user : null);
      setLoading(false);
    });
    return () => unsub();
  }, [isFirebaseEnabled]);

  const login = async (email, password) => {
    const { user } = await loginWithFirebase(email, password);
    setFirebaseUser(user);
    return user;
  };

  const logout = async () => {
    await logoutFirebase();
    setFirebaseUser(null);
  };

  const loginWithGoogle = async () => {
    const { user } = await loginWithGoogleService();
    setFirebaseUser(user);
    return user;
  };

  const loginWithApple = async () => {
    const { user } = await loginWithAppleService();
    setFirebaseUser(user);
    return user;
  };

  const value = {
    firebaseUser,
    loading,
    isFirebaseEnabled,
    login,
    logout,
    loginWithGoogle,
    loginWithApple,
  };

  return (
    <FirebaseAuthContext.Provider value={value}>
      {children}
    </FirebaseAuthContext.Provider>
  );
}

export default FirebaseAuthContext;
